<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/spray-records">Spray Records</a></li>
        <li class="breadcrumb-item active">View Record</li>
      </ol>
    </nav>
    <div class="d-flex align-items-center gap-3">
      <h1 class="h2 mb-0 fw-bold">{{ record?.fieldName || 'Spray Record' }}</h1>
      <span *ngIf="record" class="badge" [ngClass]="record.status | statusBadge">
        {{ getStatusText(record.status) }}
      </span>
    </div>
    <p class="text-muted mb-0 mt-1" *ngIf="record">
      {{ record.applicationDate | adnDate }}
    </p>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-outline-secondary" (click)="downloadPdf()" title="PDF export coming soon" disabled>
      <i class="bi bi-file-pdf me-2"></i>Download PDF
    </button>
    <button class="btn btn-outline-primary" (click)="editRecord()">
      <i class="bi bi-pencil me-2"></i>Edit
    </button>
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="bi bi-arrow-left me-2"></i>Back
    </button>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading spray record...</p>
</div>

<!-- Error State -->
<div *ngIf="!isLoading && errorMessage" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
</div>

<!-- Main Content -->
<div *ngIf="!isLoading && record" class="row g-4">
  <!-- Left Column - Main Content -->
  <div class="col-lg-8">

    <!-- Section 1: Application Details -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Application Details</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Date</label>
            <p class="mb-0 fw-medium">{{ record.applicationDate | adnDate }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Status</label>
            <p class="mb-0">
              <span class="badge" [ngClass]="record.status | statusBadge">
                {{ getStatusText(record.status) }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Field Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Field Information</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Customer</label>
            <p class="mb-0">
              <a *ngIf="customer" [routerLink]="['/customers', customer.id, 'view']" class="text-decoration-none fw-medium">
                {{ customer.name }}
              </a>
              <span *ngIf="!customer" class="text-muted">—</span>
            </p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Field Name</label>
            <p class="mb-0 fw-medium">{{ field?.name || record.fieldName }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Acreage</label>
            <p class="mb-0">{{ field?.acreage || record.fieldAcreage }} ac</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Crop Type</label>
            <p class="mb-0">{{ field?.cropType || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Address</label>
            <p class="mb-0">{{ field?.address || '—' }}</p>
          </div>

          <!-- Coordinates -->
          <div class="col-12" *ngIf="field?.latitude && field?.longitude">
            <hr class="my-2">
            <div class="d-flex align-items-center gap-2 mb-2">
              <label class="form-label text-muted small mb-0">
                <i class="bi bi-pin-map me-1"></i>Coordinates
              </label>
              <button type="button" class="btn btn-link btn-sm p-0" (click)="toggleCoordinateDisplay()" title="Toggle format">
                <i class="bi bi-arrow-repeat small"></i>
              </button>
              <button type="button" class="btn btn-link btn-sm p-0" (click)="openInMaps()" title="View on Google Maps">
                <i class="bi bi-box-arrow-up-right small"></i>
              </button>
            </div>
            <p class="mb-0 font-monospace">{{ formatCoordinates(field?.latitude ?? null, field?.longitude ?? null) }}</p>
            <small class="text-muted">{{ coordinateDisplayMode === 'decimal' ? 'Decimal degrees' : 'Degrees, minutes, seconds' }}</small>
          </div>

          <!-- Address -->
          <div class="col-12" *ngIf="field?.address">
            <label class="form-label text-muted small mb-1">
              <i class="bi bi-building me-1"></i>Address
            </label>
            <p class="mb-0">{{ field?.address }}</p>
          </div>

          <!-- Load/Mix Location -->
          <div class="col-12" *ngIf="record.loadMixLocation && !record.loadMixLocation.useLocationForLoadMixLocation">
            <hr class="my-2">
            <label class="form-label text-muted small mb-1">Load/Mix Location</label>
            <p class="mb-0 font-monospace">
              {{ formatCoordinates(record.loadMixLocation.loadMixLocationLatitude || null, record.loadMixLocation.loadMixLocationLongitude || null) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 3: Spray Details -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-droplet me-2"></i>Spray Details</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">What Was Treated</label>
            <p class="mb-0 fw-medium">{{ record.sprayDetails?.whatWasTreated || 'N/A' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Target Pest</label>
            <p class="mb-0 fw-medium">{{ record.sprayDetails?.targetPest || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Nozzle Description</label>
            <p class="mb-0">{{ record.sprayDetails?.nozzleDescription || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Nozzle to Target Distance</label>
            <p class="mb-0">{{ record.sprayDetails?.nozzleToTargetDistance || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Pump Pressure</label>
            <p class="mb-0">{{ record.sprayDetails?.pumpPressure || 'N/A' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Travel Speed</label>
            <p class="mb-0">{{ record.sprayDetails?.travelSpeed || 'N/A' }}</p>
          </div>

          <!-- Drift Management -->
          <div class="col-12" *ngIf="record.sprayDetails?.descriptionOfDriftManagementPractices">
            <label class="form-label text-muted small mb-1">Drift Management Practices</label>
            <p class="mb-0">{{ record.sprayDetails?.descriptionOfDriftManagementPractices }}</p>
          </div>

          <!-- Application Types -->
          <div class="col-12" *ngIf="getApplicationTypeLabels().length > 0">
            <hr class="my-2">
            <label class="form-label text-muted small mb-1">Application Types</label>
            <div class="d-flex flex-wrap gap-2">
              <span *ngFor="let type of getApplicationTypeLabels()" class="badge rounded-pill bg-secondary-subtle text-secondary">
                {{ type }}
              </span>
            </div>
            <p class="mb-0 mt-2" *ngIf="record.applicationTypeInfo?.applicationTypeOther">
              <span class="text-muted">Other:</span> {{ record.applicationTypeInfo?.applicationTypeOther }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 4: Chemical Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-flask me-2"></i>Chemical Information</h5>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-striped table-hover mb-0">
            <thead>
              <tr>
                <th class="ps-3">Chemical Name</th>
                <th>EPA Number</th>
                <th>Rate</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let chemical of record.chemicals; trackBy: trackByChemicalId">
                <td class="ps-3">
                  <span class="fw-medium">{{ chemical.name }}</span>
                  <div class="small text-muted" *ngIf="chemical.templateName">
                    From: {{ chemical.templateName }}
                  </div>
                </td>
                <td>{{ chemical.epaNumber }}</td>
                <td>{{ chemical.rate }}</td>
                <td>
                  <span class="badge" [ngClass]="getSourceBadgeClass(chemical.source)">
                    {{ getSourceLabel(chemical.source) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Total Volume and Tank Mix Notes -->
        <div class="p-3 border-top" *ngIf="record.totalVolume || record.tankMixNotes">
          <div class="row g-3">
            <div class="col-md-4" *ngIf="record.totalVolume">
              <label class="form-label text-muted small mb-1">Total Volume</label>
              <p class="mb-0 fw-medium">{{ record.totalVolume }}</p>
            </div>
            <div class="col-md-8" *ngIf="record.tankMixNotes">
              <label class="form-label text-muted small mb-1">Tank Mix Notes</label>
              <p class="mb-0">{{ record.tankMixNotes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 5: Certification -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-award me-2"></i>Certification</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Certification Name</label>
            <p class="mb-0 fw-medium">{{ certification?.name || record.certificationName }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Certificate Number</label>
            <p class="mb-0">{{ certification?.certNumber || 'N/A' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">State</label>
            <p class="mb-0">{{ certification?.state || 'N/A' }}</p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Expiration Date</label>
            <p class="mb-0" [class.text-danger]="isCertificationExpired()" [class.text-warning]="isCertificationExpiringSoon()">
              <ng-container *ngIf="certification?.expirationDate">
                {{ certification?.expirationDate | adnDate }}
                <i *ngIf="isCertificationExpiringSoon()" class="bi bi-exclamation-triangle ms-1" title="Expiring soon"></i>
                <i *ngIf="isCertificationExpired()" class="bi bi-x-circle ms-1" title="Expired"></i>
              </ng-container>
              <span *ngIf="!certification?.expirationDate" class="text-muted">N/A</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 6: Visual Observer & NOTAM -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-eye me-2"></i>Visual Observer & NOTAM</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Visual Observer</label>
            <p class="mb-0">{{ record.visualObserverInfo?.visualObserver || 'N/A' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">VO Tested</label>
            <p class="mb-0">
              <ng-container *ngIf="record.visualObserverInfo?.voTested">
                <i class="bi bi-check-circle-fill text-success me-1"></i>Yes
              </ng-container>
              <ng-container *ngIf="!record.visualObserverInfo?.voTested">
                <span class="text-muted">Not tested</span>
              </ng-container>
            </p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">NOTAM Number</label>
            <p class="mb-0">{{ record.visualObserverInfo?.notam || 'N/A' }}</p>
          </div>
          <div class="col-12">
            <small class="text-muted">
              <i class="bi bi-telephone me-1"></i>Flight Services: <a href="tel:+18774876867">1-877-487-6867</a>
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 7: Drone Flights -->
    <div class="card mb-4">
      <div class="card-header p-3 d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="icon-drone me-2"></i>Drone Flights</h5>
        <button *ngIf="getAllFlightPaths().length > 1" type="button" class="btn btn-sm btn-outline-primary" (click)="viewAllFlightPaths()">
          <i class="bi bi-map me-1"></i>View All Flights
        </button>
      </div>
      <div class="card-body">
        <div *ngFor="let flight of record.droneFlights; let i = index; let last = last; trackBy: trackByFlightId" class="drone-flight-item">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div class="d-flex align-items-center gap-2">
              <span class="badge bg-primary rounded-pill">{{ i + 1 }}</span>
              <div>
                <h6 class="mb-0">{{ getDroneName(flight.droneId) }}</h6>
                <small class="text-muted">N-Number: {{ getDroneNNumber(flight.droneId) }}</small>
              </div>
            </div>
            <button *ngIf="flight.flightPath" type="button" class="btn btn-sm btn-outline-secondary" (click)="viewFlightPath(flight)">
              <i class="bi bi-map me-1"></i>View Map
            </button>
          </div>

          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Pilot</label>
              <p class="mb-0">{{ getPilotName(flight.pilotId) }}</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">Start Time</label>
              <p class="mb-0">{{ formatTime(flight.startTime) }}</p>
            </div>
            <div class="col-md-4">
              <label class="form-label text-muted small mb-1">End Time</label>
              <p class="mb-0">{{ formatTime(flight.endTime) }}</p>
            </div>
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">Altitude</label>
              <p class="mb-0">
                <ng-container *ngIf="flight.flightAltitude != null">{{ flight.flightAltitude }} ft</ng-container>
                <span *ngIf="flight.flightAltitude == null" class="text-muted">N/A</span>
              </p>
            </div>
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">Speed</label>
              <p class="mb-0">
                <ng-container *ngIf="flight.flightSpeed != null">{{ flight.flightSpeed }} mph</ng-container>
                <span *ngIf="flight.flightSpeed == null" class="text-muted">N/A</span>
              </p>
            </div>
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">Acreage Sprayed</label>
              <p class="mb-0 fw-medium">{{ flight.acreageSprayed | number:'1.1-1' }} ac</p>
            </div>
            <!-- FAA §F.2.d. Tested with `== null`, never `if (flight.numFlights)`:
                 a real 0 is a distinct fact from "not entered" and must render
                 as 0, not fall into the not-entered branch. -->
            <div class="col-md-3">
              <label class="form-label text-muted small mb-1">Number of Flights</label>
              <p class="mb-0">
                <span *ngIf="flight.numFlights != null" class="fw-medium">{{ flight.numFlights }}</span>
                <span *ngIf="flight.numFlights == null" class="text-muted fst-italic">
                  Not entered &mdash; filed as 1
                </span>
              </p>
            </div>
            <div class="col-md-3" *ngIf="flight.flightPath">
              <label class="form-label text-muted small mb-1">Flight Path</label>
              <p class="mb-0">
                <i class="bi bi-check-circle-fill text-success me-1"></i>
                {{ flight.flightPath.name }}
              </p>
            </div>

            <!-- DJI's record of this aircraft's flights (ADN-2307); nothing renders without DJI data -->
            <div class="col-12" *ngIf="djiFlightsFor(flight).length > 0">
              <app-dji-flight-comparison [logged]="flight" [djiFlights]="djiFlightsFor(flight)" (refresh)="reloadDjiFlights()"></app-dji-flight-comparison>
            </div>

            <!-- Chemical Usage for this Drone -->
            <div class="col-12" *ngIf="flight.chemicalUsage.length > 0">
              <label class="form-label text-muted small mb-1">Chemical Usage</label>
              <div class="chemical-usage-list p-2 rounded bg-body-tertiary">
                <div *ngFor="let cu of flight.chemicalUsage" class="d-flex justify-content-between py-1 border-bottom">
                  <span class="small">{{ cu.chemicalName }}</span>
                  <span class="small fw-medium">{{ cu.amountUsed }}</span>
                </div>
              </div>
            </div>
          </div>

          <hr *ngIf="!last" class="my-4">
        </div>
      </div>
    </div>

    <!-- DJI flights whose aircraft has no logged drone flight on this record (ADN-2307) -->
    <app-dji-flight-data
      [flights]="unmatchedDjiFlights"
      title="Other DJI flights linked to this record"
      (refresh)="reloadDjiFlights()">
    </app-dji-flight-data>

    <!-- Section 8: Weather Conditions -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-cloud-sun me-2"></i>Weather Conditions</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label text-muted small mb-1">
              <i class="bi bi-thermometer me-1"></i>Temperature
            </label>
            <p class="mb-0 fw-medium">
              <ng-container *ngIf="record.weather?.temperature != null">{{ record.weather.temperature }}&deg;F</ng-container>
              <span *ngIf="record.weather?.temperature == null" class="text-muted">N/A</span>
            </p>
          </div>
          <div class="col-md-3">
            <label class="form-label text-muted small mb-1">
              <i class="bi bi-droplet me-1"></i>Humidity
            </label>
            <p class="mb-0">
              <ng-container *ngIf="record.weather?.humidity != null">{{ record.weather.humidity }}%</ng-container>
              <span *ngIf="record.weather?.humidity == null" class="text-muted">N/A</span>
            </p>
          </div>
          <div class="col-md-3">
            <label class="form-label text-muted small mb-1">
              <i class="bi bi-wind me-1"></i>Wind Speed
            </label>
            <p class="mb-0">
              <ng-container *ngIf="record.weather?.windSpeed != null">{{ record.weather.windSpeed }} mph</ng-container>
              <span *ngIf="record.weather?.windSpeed == null" class="text-muted">N/A</span>
            </p>
          </div>
          <div class="col-md-3">
            <label class="form-label text-muted small mb-1">Wind Direction</label>
            <p class="mb-0">
              <ng-container *ngIf="record.weather?.windDirection">{{ record.weather.windDirection }}</ng-container>
              <span *ngIf="!record.weather?.windDirection" class="text-muted">N/A</span>
            </p>
          </div>
          <div class="col-12" *ngIf="record.weather.conditions">
            <label class="form-label text-muted small mb-1">Conditions</label>
            <p class="mb-0">{{ record.weather.conditions }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 9: Incident Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-exclamation-triangle me-2"></i>Incident Information</h5>
      </div>
      <div class="card-body">
        <ng-container *ngIf="!hasAnyIncidents">
          <div class="text-center py-3">
            <i class="bi bi-check-circle-fill text-success fs-2rem"></i>
            <p class="mb-0 mt-2 text-muted">No incidents reported</p>
          </div>
        </ng-container>

        <ng-container *ngIf="hasAnyIncidents">
          <!-- Takeoff/Landing Damage -->
          <div *ngIf="record.incidentInfo?.takeOffOrLandingDamage" class="mb-3">
            <div class="d-flex align-items-center gap-2 mb-2">
              <i class="bi bi-exclamation-circle text-warning"></i>
              <span class="fw-medium">Take Off or Landing Damage</span>
            </div>
            <p class="mb-0 ps-4" *ngIf="record.incidentInfo?.descriptionOfTakeOffOrLandingDamage">
              {{ record.incidentInfo?.descriptionOfTakeOffOrLandingDamage }}
            </p>
          </div>

          <!-- Equipment Malfunction -->
          <div *ngIf="record.incidentInfo?.equipmentMalfunction" class="mb-3">
            <div class="d-flex align-items-center gap-2 mb-2">
              <i class="bi bi-gear text-warning"></i>
              <span class="fw-medium">Equipment Malfunction</span>
              <span *ngIf="record.incidentInfo?.equipmentMalfunctionType" class="badge bg-warning-subtle text-warning">
                {{ record.incidentInfo?.equipmentMalfunctionType }}
              </span>
            </div>
            <p class="mb-0 ps-4" *ngIf="record.incidentInfo?.descriptionOfEquipmentMalfunction">
              {{ record.incidentInfo?.descriptionOfEquipmentMalfunction }}
            </p>
          </div>

          <!-- Accident -->
          <div *ngIf="record.incidentInfo?.hasAccident">
            <div class="d-flex align-items-center gap-2 mb-2">
              <i class="bi bi-exclamation-triangle-fill text-danger"></i>
              <span class="fw-medium">Accident</span>
              <span *ngIf="record.incidentInfo?.accidentType" class="badge bg-danger-subtle text-danger">
                {{ record.incidentInfo?.accidentType }}
              </span>
            </div>
            <p class="mb-0 ps-4" *ngIf="record.incidentInfo?.accidentDescription">
              {{ record.incidentInfo?.accidentDescription }}
            </p>
            <p class="mb-0 ps-4 mt-2" *ngIf="record.incidentInfo?.uaLossType">
              <span class="text-muted">UA Loss Type:</span> {{ record.incidentInfo?.uaLossType }}
            </p>
          </div>

          @if (canGenerateReports()) {
          <!-- Incident Report  -->
          <div class="border-top pt-3 mt-3 d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div>
              <div class="fw-medium"><i class="bi bi-file-earmark-text me-2"></i>Incident Report</div>
              <p class="small text-muted mb-0">Create a PDF of the incidents on this record.</p>
            </div>

            <!-- Existing report: offer to replace it; otherwise offer to generate one -->
            @if (recentReport(); as existingReport) {
              <!-- Replace Button -->
              <button class="btn btn-sm btn-outline-primary"
                (click)="reportToReplace.set(existingReport)"
                [disabled]="isGenerating()">
                <ng-container *ngIf="!isGenerating()">
                  <i class="bi bi-file-earmark-pdf me-1"></i>Regenerate Report
                </ng-container>
                <ng-container *ngIf="isGenerating()">
                  <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Generating...
                </ng-container>
              </button>
            } @else {
              <!-- Generate Button -->
              <button class="btn btn-sm btn-outline-primary"
                (click)="generateReport()"
                [disabled]="isGenerating()">
                <ng-container *ngIf="!isGenerating()">
                  <i class="bi bi-file-earmark-pdf me-1"></i>Generate Report
                </ng-container>
                <ng-container *ngIf="isGenerating()">
                  <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Generating...
                </ng-container>
              </button>
            }

          </div>

          <!-- Generated Report -->
          <div class="card mt-2">
             <!-- Error message for report generation -->
            <div *ngIf="reportError()" class="alert alert-danger alert-dismissible fade show mb-3" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ reportError() }}
              <button type="button" class="btn-close" (click)="clearError()"></button>
              <div class="mt-2">
                <button class="btn btn-sm btn-outline-danger" (click)="retryLoad()">
                  <i class="bi bi-arrow-clockwise me-1"></i>Retry
                </button>
              </div>
            </div>
            
            <div class="card-header d-flex justify-content-between align-items-center p-3">
              <h6 class="mb-0">Generated Report</h6>
            </div>

            <!-- Report (one per spray record) -->
            @if (recentReport(); as report) {
              <app-drone-report-item
                    class="d-block"
                    [report]="report"
                    (download)="downloadReport(report)"
                    (retry)="retryReport(report)"
                    (remove)="reportToDelete.set(report)">
              </app-drone-report-item>
            } @else {
              <!-- Empty State -->
              <div class="px-1 pt-1 pb-3 text-center">
                <div class="empty-state-icon mb-2" style="width: 48px; height: 48px;">
                  <i class="bi bi-file-earmark-text fs-5"></i>
                </div>
                <h6 class="mb-1">No Report Generated</h6>
                <p class="small text-muted mb-0">No report has been generated for this incident yet.</p>
                <button class="btn btn-sm btn-outline-primary mt-3"
                    (click)="generateReport()"
                    [disabled]="isGenerating()">
                    <ng-container *ngIf="!isGenerating()">
                      <i class="bi bi-plus-lg me-1"></i>Generate Report
                    </ng-container>
                    <ng-container *ngIf="isGenerating()">
                      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                      Generating...
                    </ng-container>
                  </button>
              </div>
            }
          </div>
          }
        </ng-container>
      </div>
    </div>

    <!-- Section 10: Safety & Compliance -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-shield-check me-2"></i>Safety & Compliance</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Re-Entry Interval</label>
            <p class="mb-0 fw-medium">{{ record.safetyCompliance?.reentryInterval || 'N/A' }}</p>
          </div>
          <div class="col-md-8">
            <label class="form-label text-muted small mb-1">PPE Required</label>
            <div class="d-flex flex-wrap gap-2" *ngIf="getPpeLabels().length > 0">
              <span *ngFor="let ppe of getPpeLabels()" class="badge rounded-pill bg-info-subtle text-info">
                {{ ppe }}
              </span>
            </div>
            <p class="mb-0 text-muted" *ngIf="getPpeLabels().length === 0">None specified</p>
          </div>
          <div class="col-12" *ngIf="record.safetyCompliance?.rinseEquipmentPostApplication">
            <label class="form-label text-muted small mb-1">Equipment Rinsing Procedures</label>
            <p class="mb-0">{{ record.safetyCompliance?.rinseEquipmentPostApplication }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 11: Emergency Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-telephone me-2"></i>Emergency Information</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Emergency Contact Number</label>
            <p class="mb-0">
              <a *ngIf="record.emergencyContact?.emergencyContactNumber"
                 [href]="'tel:' + record.emergencyContact?.emergencyContactNumber"
                 class="text-decoration-none">
                <i class="bi bi-telephone me-1"></i>{{ formatPhone(record.emergencyContact?.emergencyContactNumber) }}
              </a>
              <span *ngIf="!record.emergencyContact?.emergencyContactNumber" class="text-muted">N/A</span>
            </p>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">State Emergency Contact</label>
            <p class="mb-0">
              <a *ngIf="record.emergencyContact?.stateEmergencyContactNumber"
                 [href]="'tel:' + record.emergencyContact?.stateEmergencyContactNumber"
                 class="text-decoration-none">
                <i class="bi bi-telephone me-1"></i>{{ formatPhone(record.emergencyContact?.stateEmergencyContactNumber) }}
              </a>
              <span *ngIf="!record.emergencyContact?.stateEmergencyContactNumber" class="text-muted">N/A</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 12: Additional Notes -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
      </div>
      <div class="card-body">
        <p class="mb-0" *ngIf="record.notes">{{ record.notes }}</p>
        <p class="mb-0 text-muted fst-italic" *ngIf="!record.notes">No additional notes</p>
      </div>
    </div>

  </div>

  <!-- Right Column - Summary Sidebar -->
  <div class="col-lg-4">
    <div class="sticky-top sticky-sidebar">
    <div class="card summary-card">
      <div class="card-header p-3">
        <h5 class="mb-0">Record Summary</h5>
      </div>
      <div class="card-body">
        <!-- Field Info -->
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small">Field</div>
          <div class="summary-value fw-medium">{{ field?.name || record.fieldName }}</div>
        </div>
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small">Total Acreage</div>
          <div class="summary-value">{{ field?.acreage || record.fieldAcreage }} acres</div>
        </div>

        <!-- Drones Summary -->
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small">Drones ({{ record.droneFlights.length }})</div>
          <div class="summary-drones mt-1">
            <div *ngFor="let flight of record.droneFlights; trackBy: trackByFlightId" class="d-flex justify-content-between small py-1 border-bottom border-light">
              <span class="text-truncate mw-140px" [title]="getDroneName(flight.droneId)">
                {{ getDroneName(flight.droneId) }}
              </span>
              <span class="text-muted">{{ flight.acreageSprayed | number:'1.1-1' }} ac</span>
            </div>
          </div>
        </div>

        <!-- Total Acreage Sprayed -->
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small">Total Sprayed</div>
          <div class="summary-value fw-medium text-primary">{{ totalAcreageSprayed | number:'1.1-1' }} acres</div>
        </div>

        <!-- Chemicals Summary -->
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small">Chemicals ({{ record.chemicals.length }})</div>
          <div class="summary-chemicals mt-1">
            <div *ngFor="let chem of record.chemicals; trackBy: trackByChemicalId" class="d-flex justify-content-between small py-1 border-bottom border-light">
              <span class="text-truncate mw-140px" [title]="chem.name">{{ chem.name }}</span>
              <span class="text-muted">{{ chem.rate }}</span>
            </div>
          </div>
        </div>

        <!-- Total Chemical Usage -->
        <div class="summary-item mb-3" *ngIf="totalChemicalUsage.length > 0">
          <div class="summary-label text-muted small d-flex align-items-center gap-1">
            Total Usage
            <i class="bi bi-info-circle" title="Calculated from acreage × rate for all drones"></i>
          </div>
          <div class="summary-usage mt-1">
            <div *ngFor="let usage of totalChemicalUsage; trackBy: trackByIndex" class="d-flex justify-content-between small py-1 border-bottom border-light">
              <span class="text-truncate mw-120px" [title]="usage.chemicalName">{{ usage.chemicalName }}</span>
              <span class="fw-medium text-primary">{{ usage.totalAmount }}</span>
            </div>
          </div>
        </div>

        <!-- Certification -->
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small">Certification</div>
          <div class="summary-value">{{ certification?.name || record.certificationName }}</div>
          <div class="small" *ngIf="certification?.expirationDate" [class.text-danger]="isCertificationExpired()" [class.text-warning]="isCertificationExpiringSoon()">
            <ng-container *ngIf="isCertificationExpired()">
              <i class="bi bi-x-circle me-1"></i>Expired
            </ng-container>
            <ng-container *ngIf="isCertificationExpiringSoon()">
              <i class="bi bi-exclamation-triangle me-1"></i>Expires in {{ getCertificationDaysUntilExpiration() }} days
            </ng-container>
            <ng-container *ngIf="!isCertificationExpired() && !isCertificationExpiringSoon()">
              Expires: {{ certification?.expirationDate | adnDate }}
            </ng-container>
          </div>
          <div class="small text-muted" *ngIf="certification?.certNumber && certification?.certNumber !== 'N/A'">
            #{{ certification?.certNumber }}
          </div>
        </div>

        <hr class="my-3">

        <!-- Compliance Checklist Status -->
        <div class="summary-item mb-3">
          <div class="summary-label text-muted small mb-2">Compliance Status</div>
          <div class="d-flex flex-column gap-2">
            <div class="d-flex align-items-center gap-2">
              <i class="bi" [ngClass]="record.preFlightChecklist ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"></i>
              <span class="small">Pre-flight checklist</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <i class="bi" [ngClass]="record.bufferZonesVerified ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"></i>
              <span class="small">Buffer zones verified</span>
            </div>
          </div>
        </div>

        <hr class="my-3">

        <!-- Action Buttons -->
        <div class="d-grid gap-2">
          <button type="button" class="btn btn-primary" (click)="editRecord()">
            <i class="bi bi-pencil me-2"></i>Edit Record
          </button>
          <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
            <i class="bi bi-arrow-left me-2"></i>Back to List
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</div>

<!-- Flight Map Modal -->
<div *ngIf="showFlightMapModal" class="flight-map-modal-overlay" (click)="closeFlightMapModal()">
  <div class="flight-map-modal" (click)="$event.stopPropagation()">
    <div class="flight-map-modal-header">
      <h5 class="mb-0">
        <i class="bi bi-map me-2"></i>
        <ng-container *ngIf="flightMapModalFlight">{{ flightMapModalFlight.flightPath?.name || 'Flight Path' }}</ng-container>
        <ng-container *ngIf="!flightMapModalFlight">All Flight Paths</ng-container>
      </h5>
      <button type="button" class="btn-close" (click)="closeFlightMapModal()"></button>
    </div>
    <div class="flight-map-modal-body">
      <app-flight-map
        [flightPath]="flightMapModalFlight?.flightPath ?? null"
        [flightPaths]="flightMapModalFlight ? [] : getAllFlightPaths()"
        [fieldCenter]="getFieldCenter()"
        [showUploadOverlay]="false"
        [height]="'500px'">
      </app-flight-map>
    </div>
    <div class="flight-map-modal-footer" *ngIf="flightMapModalFlight?.flightPath as path">
      <div class="d-flex justify-content-between align-items-center">
        <div class="small text-muted">
          <i class="bi bi-file-earmark me-1"></i>{{ path.fileName }}
          <span class="mx-2">•</span>
          <i class="bi bi-rulers me-1"></i>{{ path.areaAcres | number:'1.1-1' }} acres
        </div>
        <button type="button" class="btn btn-primary btn-sm" (click)="closeFlightMapModal()">
          Done
        </button>
      </div>
    </div>
  </div>
</div>

@if(reportToDelete(); as report) {
 <app-drone-report-confirm-modal
    [report]="report"
    [action]="deleteReport"
    (closed)="reportToDelete.set(null)" />
}

@if(reportToReplace(); as report) {
 <app-drone-report-confirm-modal
    [report]="report"
    [action]="replaceReport"
    title="Replace Report"
    message="Are you sure you want to replace this report?"
    warning="The existing report will be deleted and a new one generated"
    confirmButtonText="Replace"
    buttonLoadingText="Replacing..."
    confirmIcon="bi-arrow-repeat"
    (closed)="reportToReplace.set(null)" />
}
